import { Injectable, inject, signal } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";

export interface Crumb {
	label: string;
	url: string;
}

/** A segment whose label is not known from the route — an id resolved at runtime. */
export interface DynamicCrumb {
	/** Shown until the real label is registered. Never the raw id. */
	fallback: string;
}

/**
 * One breadcrumb entry per URL segment, or a single string to label the whole
 * route node at once.
 */
export type CrumbSpec = string | DynamicCrumb | Array<string | DynamicCrumb | null>;

/**
 * Breadcrumbs are declared as `data: { breadcrumb: … }` on each route.
 *
 * A route's `path` can hold several segments — `"organizations/:id"` is one
 * route node covering two — so the spec may be an array with one entry per
 * segment. A `null` entry contributes to the URL without producing a crumb.
 *
 * A DynamicCrumb marks a segment whose label is only known at runtime, such as
 * an organization id. `setLabel` supplies the real name; until then the
 * declared fallback shows. A raw Firebase id is never displayed — it is
 * meaningless to a reader and looks like a bug.
 */
@Injectable({ providedIn: "root" })
export class BreadcrumbService {
	private readonly router = inject(Router);
	private readonly labels = new Map<string, string>();

	private readonly crumbsState = signal<Crumb[]>([]);
	readonly crumbs = this.crumbsState.asReadonly();

	constructor() {
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => this.refresh());
		this.refresh();
	}

	/** Supply a human label for a dynamic segment, e.g. an org id to its name. */
	setLabel(paramValue: string, label: string): void {
		this.labels.set(paramValue, label);
		this.refresh();
	}

	build(root: ActivatedRouteSnapshot): Crumb[] {
		const crumbs: Crumb[] = [];
		let url = "";
		let node: ActivatedRouteSnapshot | null = root;

		while (node) {
			const segments = node.url.map((s) => s.path);
			const declared = node.data["breadcrumb"] as CrumbSpec | undefined;

			if (Array.isArray(declared)) {
				// One entry per segment, so a multi-segment route path such as
				// "organizations/:id" produces a crumb for each of its parts and
				// each dynamic entry keys on its own segment.
				segments.forEach((segment, index) => {
					url += `/${segment}`;
					const label = this.resolve(declared[index] ?? null, segment);
					if (label) {
						crumbs.push({ label, url });
					}
				});
			} else if (segments.length > 0) {
				const segment = segments.join("/");
				url += `/${segment}`;
				const label = this.resolve(declared ?? null, segments[segments.length - 1]);
				if (label) {
					crumbs.push({ label, url });
				}
			}
			// A node contributing no URL segment produces no crumb. Angular's
			// default paramsInheritanceStrategy copies a parent's data onto an
			// empty-path child, so without this an index child repeats its
			// parent's label — "Organizations › Noldor › Details › Details".

			node = node.firstChild;
		}

		return crumbs;
	}

	/** Returns the label for one entry, or null when it should produce no crumb. */
	private resolve(entry: string | DynamicCrumb | null, segment: string): string | null {
		if (!entry) {
			return null;
		}
		if (typeof entry === "string") {
			return entry;
		}
		return this.labels.get(segment) ?? entry.fallback;
	}

	private refresh(): void {
		this.crumbsState.set(this.build(this.router.routerState.snapshot.root));
	}
}
